import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  fetchAdminTenancyTermination,
  upsertAdminTenancyTermination,
  withdrawAdminTenancyTermination,
} from "../../../../api/adminData";
import { SECONDARY_BUTTON } from "../../tenancies/workspaceUi";
import TerminationConfirmationPanel from "./TerminationConfirmationPanel";
import TerminationForm from "./TerminationForm";
import {
  LegacyTerminationSummary,
  RecordedTerminationSummary,
  withdrawnAtText,
} from "./TerminationSummary";
import WithdrawTerminationDialog from "./WithdrawTerminationDialog";
import {
  EMPTY_TERMINATION_FORM,
  buildTerminationPayload,
  formFromResponse,
  isCancelledTenancy,
  isNotYetStartedTenancy,
  noticePeriodMonths,
  noticePeriodText,
  terminationViewState,
  validateTerminationForm,
} from "./terminationModel";

// Foreground text with a destructive marker — see ERROR in TerminationForm.jsx.
const LOAD_ERROR =
  "m-0 border-l-2 border-destructive pl-2 text-[12.5px] text-foreground";

/**
 * KND-2 — "Kündigung", inside the Mietvertrag card of one tenancy.
 *
 * THE ONE FRONTEND OWNER of `notice_given_at`, `termination_effective_date`
 * and `terminated_by` (discovery E2). "Bearbeiten" no longer shows or sends
 * them; everything that writes them from this page goes through the three
 * KND-1 commands, which also keep the termination record and the tenancy in
 * one transaction (E3).
 *
 * THE SERVER IS THE SOURCE OF TRUTH. One GET when the card mounts — only an
 * EXPANDED tenancy renders this, so a collapsed one costs nothing. A save or a
 * withdrawal answers with the same response shape, so the block shows exactly
 * what the server stored without a second request; only a response without a
 * `state` (never expected) falls back to one GET. Nothing is written on
 * render, and no local draft survives a successful save.
 *
 * PESSIMISTIC. The form stays open (with what was typed) until the server
 * accepted it; a refusal is a toast and the form is untouched. The withdrawal
 * dialog behaves the same way.
 *
 * AFTER A WRITE the page reloads the tenancy rows (badge, group and end
 * change) and the Verlauf (both commands write tenant + unit audit entries —
 * Regel 49) through `onChanged`.
 *
 * KÜNDIGUNGSBESTÄTIGUNG (KND-3c) lives in its own container,
 * `TerminationConfirmationPanel`, rendered here once a termination record
 * exists (no record, no confirmation). It sits at one place in the block, so
 * opening or closing the form never remounts it; a save or a withdrawal bumps
 * its `refreshToken` — one GET, because a save may have generated the first
 * version on the server. The panel gets no `onChanged`: generating a
 * confirmation does not reload the page.
 */
export default function TenancyTerminationSection({
  tn,
  terminationMaxIso = null,
  onChanged,
}) {
  const tenancyId = tn?.id != null ? String(tn.id) : null;

  const [response, setResponse] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [formMode, setFormMode] = useState(null); // null | "create" | "edit" | "adopt"
  const [form, setForm] = useState(EMPTY_TERMINATION_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [confirmationRefresh, setConfirmationRefresh] = useState(0);

  // A request that answers after the card was closed must not set state.
  // Re-armed in the effect body, so a StrictMode re-mount does not leave it
  // false for the rest of the component's life.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Where focus goes when the form or the dialog closes after a write: the
  // control that opened it may no longer exist in the new state.
  const headingRef = useRef(null);
  const withdrawButtonRef = useRef(null);
  const focusHeadingOnCloseRef = useRef(false);

  useEffect(() => {
    if (!tenancyId) return undefined;
    let current = true;
    // Another tenancy's answer must never stand in for this one's.
    setResponse(null);
    setFormMode(null);
    setLoadState("loading");
    (async () => {
      try {
        const data = await fetchAdminTenancyTermination(tenancyId);
        if (!current) return;
        if (!data || !data.state) throw new Error("no state");
        setResponse(data);
        setLoadState("ready");
      } catch {
        if (!current) return;
        setLoadState("error");
      }
    })();
    return () => {
      current = false;
    };
  }, [tenancyId]);

  /** The write's own answer; one GET only if it carried no state. */
  const settle = useCallback(
    async (data) => {
      if (data && data.state) return data;
      return fetchAdminTenancyTermination(tenancyId);
    },
    [tenancyId]
  );

  const notifyChanged = useCallback(() => {
    if (!onChanged) return;
    Promise.resolve()
      .then(() => onChanged())
      .catch(() => {});
  }, [onChanged]);

  const openForm = (mode) => {
    // Only a legacy termination the tenant is RECORDED as having given may
    // be taken over. Defence in depth: the button exists only in that state.
    if (mode === "adopt" && terminationViewState(response) !== "legacy_tenant") {
      return;
    }
    setForm(formFromResponse(response));
    setErrors({});
    setFormMode(mode);
  };

  const closeForm = () => {
    setFormMode(null);
    setErrors({});
    // The form's own controls unmount with it; keep the keyboard in the block.
    requestAnimationFrame(() => headingRef.current?.focus());
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (saving || !tenancyId) return;
    const fieldErrors = validateTerminationForm(form);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setSaving(true);
    let saved = null;
    try {
      saved = await upsertAdminTenancyTermination(
        tenancyId,
        buildTerminationPayload(form)
      );
    } catch (err) {
      if (mountedRef.current) {
        setSaving(false);
        toast.error(err?.message || "Kündigung konnte nicht gespeichert werden.");
      }
      return;
    }
    // Saved. From here on nothing may report the save as failed.
    let next = null;
    try {
      next = await settle(saved);
    } catch {
      next = null;
    }
    if (mountedRef.current) {
      setSaving(false);
      if (next) {
        setResponse(next);
        setLoadState("ready");
      } else {
        setLoadState("error");
      }
      setConfirmationRefresh((n) => n + 1);
      closeForm();
      toast.success("Kündigung gespeichert.");
    }
    notifyChanged();
  };

  const handleWithdraw = async () => {
    if (withdrawing || !tenancyId) return;
    setWithdrawing(true);
    let done = null;
    try {
      done = await withdrawAdminTenancyTermination(tenancyId);
    } catch (err) {
      if (mountedRef.current) {
        setWithdrawing(false);
        toast.error(
          err?.message || "Kündigung konnte nicht zurückgenommen werden."
        );
      }
      return;
    }
    let next = null;
    try {
      next = await settle(done);
    } catch {
      next = null;
    }
    if (mountedRef.current) {
      setWithdrawing(false);
      focusHeadingOnCloseRef.current = true;
      setWithdrawOpen(false);
      if (next) {
        setResponse(next);
        setLoadState("ready");
      } else {
        setLoadState("error");
      }
      setConfirmationRefresh((n) => n + 1);
      toast.success("Kündigung zurückgenommen.");
    }
    notifyChanged();
  };

  const cancelled = isCancelledTenancy(tn);
  const view = terminationViewState(response);
  const record = response?.termination || null;

  let badge = null;
  let body = null;

  if (!tenancyId) {
    body = null;
  } else if (loadState === "loading" && !response) {
    body = <p className="m-0 text-[12.5px] text-muted-foreground">Kündigung wird geladen …</p>;
  } else if (loadState === "error") {
    body = (
      <p role="alert" className={LOAD_ERROR}>
        Kündigung konnte nicht geladen werden.
      </p>
    );
  } else if (formMode) {
    const titles = {
      create: "Kündigung erfassen",
      edit: "Kündigung bearbeiten",
      adopt: "Bestehende Kündigung übernehmen",
    };
    body = (
      <TerminationForm
        tenancyId={tenancyId}
        title={titles[formMode]}
        intro={
          formMode === "adopt"
            ? "Wird als ordentliche Kündigung durch den Mieter gespeichert."
            : null
        }
        form={form}
        errors={errors}
        noticePeriodLabel={noticePeriodText(
          noticePeriodMonths(formMode === "edit" ? record : null, tn)
        )}
        terminationMaxIso={terminationMaxIso}
        saving={saving}
        onChange={updateField}
        onSubmit={handleSubmit}
        onCancel={closeForm}
      />
    );
  } else if (view === "recorded") {
    badge = "Erfasst";
    body = (
      <>
        <RecordedTerminationSummary response={response} tn={tn} />
        {cancelled ? null : (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => openForm("edit")}
              aria-label="Kündigung bearbeiten"
            >
              Bearbeiten
            </button>
            <button
              ref={withdrawButtonRef}
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => setWithdrawOpen(true)}
            >
              Kündigung zurücknehmen
            </button>
          </div>
        )}
      </>
    );
  } else if (
    view === "legacy_tenant" ||
    view === "legacy_other" ||
    view === "legacy_unknown"
  ) {
    badge = "Altbestand";
    const readOnlyNote =
      view === "legacy_other"
        ? "Diese Kündigung stammt nicht aus dem Mieterkündigungs-Workflow und kann hier nicht bearbeitet werden."
        : view === "legacy_unknown"
          ? "Die Kündigungspartei ist nicht dokumentiert. Diese Kündigung kann nicht über den Mieterkündigungs-Workflow übernommen werden."
          : null;
    body = (
      <>
        <p className="m-0 mb-3 text-[12.5px] font-medium text-foreground">
          Bestehende Kündigung erkannt
        </p>
        <LegacyTerminationSummary response={response} />
        {readOnlyNote ? (
          <p
            role="note"
            className="m-0 mt-3 rounded-[8px] border border-border bg-muted/40 px-3 py-2 text-[12.5px] leading-snug text-foreground"
          >
            {readOnlyNote}
          </p>
        ) : cancelled ? null : (
          <div className="mt-3 flex flex-col items-start gap-2 border-t border-border pt-3">
            <p className="m-0 text-[12px] leading-snug text-muted-foreground">
              Erfasst über „Bearbeiten“. Sie kann bewusst in den
              Mieterkündigungs-Workflow übernommen werden.
            </p>
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => openForm("adopt")}
            >
              Kündigung übernehmen
            </button>
          </div>
        )}
      </>
    );
  } else if (view === "withdrawn") {
    badge = "Zurückgenommen";
    const at = withdrawnAtText(response);
    body = (
      <div className="flex flex-col items-start gap-2">
        <p className="m-0 text-[12.5px] font-medium text-foreground">
          Kündigung zurückgenommen
        </p>
        {at ? (
          <p className="m-0 text-[12px] text-muted-foreground">{at}</p>
        ) : null}
        {cancelled ? null : (
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => openForm("create")}
          >
            Neue Kündigung erfassen
          </button>
        )}
      </div>
    );
  } else if (view !== "none") {
    // A state this client does not know is not "nothing recorded": offering
    // "Kündigung erfassen" here could overwrite something it cannot show.
    body = (
      <p role="alert" className={LOAD_ERROR}>
        Kündigung konnte nicht geladen werden.
      </p>
    );
  } else if (cancelled) {
    body = (
      <p className="m-0 text-[12.5px] text-muted-foreground">
        Ein storniertes Mietverhältnis kann nicht gekündigt werden.
      </p>
    );
  } else {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p className="m-0 text-[12.5px] text-muted-foreground">
          Noch keine Kündigung erfasst.
        </p>
        {isNotYetStartedTenancy(tn) ? (
          <p className="m-0 text-[12px] leading-snug text-muted-foreground">
            Das Mietverhältnis hat noch nicht begonnen. Soll es gar nicht
            zustande kommen, ist „Mietverhältnis stornieren“ der passende Weg.
          </p>
        ) : null}
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={() => openForm("create")}
        >
          Kündigung erfassen
        </button>
      </div>
    );
  }

  if (!tenancyId) return null;

  const headingId = `termination-heading-${tenancyId}`;
  return (
    <section
      aria-labelledby={headingId}
      data-testid={`tenancy-termination-${tenancyId}`}
      className="mt-4 border-t border-border pt-4"
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h4
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="m-0 rounded-[4px] text-[13px] font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Kündigung
        </h4>
        {badge && !formMode ? (
          <span className="rounded-full border border-border bg-muted px-2 py-[1px] text-[11px] text-muted-foreground">
            {badge}
          </span>
        ) : null}
      </div>
      {body}
      {loadState === "ready" && record ? (
        <TerminationConfirmationPanel
          tenancyId={tenancyId}
          refreshToken={confirmationRefresh}
          canGenerate={!cancelled}
        />
      ) : null}
      <WithdrawTerminationDialog
        open={withdrawOpen}
        busy={withdrawing}
        onOpenChange={setWithdrawOpen}
        onConfirm={handleWithdraw}
        onCloseAutoFocus={(event) => {
          // The dialog is controlled and has no Radix Trigger, so Radix has no
          // element to return focus to and would drop it on <body>. Return it
          // by hand: to "Kündigung zurücknehmen" after "Abbrechen" / Escape,
          // to the heading after a withdrawal (that button is gone then).
          event.preventDefault();
          const afterWithdrawal = focusHeadingOnCloseRef.current;
          focusHeadingOnCloseRef.current = false;
          const target = afterWithdrawal
            ? headingRef.current
            : withdrawButtonRef.current || headingRef.current;
          target?.focus();
        }}
      />
    </section>
  );
}
